/**
 * AddGuestModal — iter197.4. Phone-first guest add for sessions.
 *
 * Flow:
 *   1. Admin types phone (or pastes from contacts). Debounced lookup to
 *      /api/users/lookup-by-phone shows a match preview (avatar + name).
 *   2. If match: one-tap "Add <Name> to session". If user isn't already a
 *      group member, admin sees a "+ also adds to group" hint.
 *   3. If no match: "Send WhatsApp invite to +91...". Tapping opens the
 *      pre-filled WA share sheet. NO synthetic guest_xxx id is created.
 *   4. Fallback: "Add as walk-in (no phone)" link reveals a name input;
 *      walk-ins are tagged walk_in=true on the server so they can never
 *      be fined or pollute coin_balances.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  UserPlus, Loader2, CheckCircle2, MessageCircle, X, Phone, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

export default function AddGuestModal({ open, sessionId, sessionTitle, groupName, onClose, onSuccess }) {
  const [phone, setPhone] = useState('');
  // iter207 — optional name for silent-add. Defaults to empty; if user
  // taps "Add anyway" without typing one, server still falls back to
  // "Guest <last4>" so behavior is back-compat.
  const [silentName, setSilentName] = useState('');
  const [lookupState, setLookupState] = useState({ status: 'idle', user: null, invite: null });
  const [walkInMode, setWalkInMode] = useState(false);
  const [walkInName, setWalkInName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // Reset whenever modal opens.
  useEffect(() => {
    if (open) {
      setPhone('');
      setSilentName('');
      setLookupState({ status: 'idle', user: null, invite: null });
      setWalkInMode(false);
      setWalkInName('');
    }
  }, [open]);

  // Debounced phone lookup whenever phone hits >= 6 digits.
  useEffect(() => {
    if (!open || walkInMode) return;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 6) {
      setLookupState({ status: 'idle', user: null, invite: null });
      return;
    }
    setLookupState((s) => ({ ...s, status: 'loading' }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/users/lookup-by-phone', { params: { phone: digits } });
        if (r.data?.found) {
          setLookupState({ status: 'found', user: r.data.user, invite: null });
        } else if (r.data?.reason === 'self') {
          setLookupState({ status: 'self', user: null, invite: null });
        } else {
          setLookupState({
            status: 'not_registered',
            user: null,
            invite: { text: r.data?.invite_text, url: r.data?.invite_url, phone: digits },
          });
        }
      } catch (e) {
        setLookupState({ status: 'error', user: null, invite: null });
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [phone, open, walkInMode]);

  const doAddByPhone = useCallback(async (opts = {}) => {
    setSubmitting(true);
    try {
      const payload = { phone };
      if (opts.silent) {
        payload.silent = true;
        // iter207 — include the typed name so the chip shows "Rohan" not "Guest 9111".
        const trimmed = silentName.trim();
        if (trimmed) payload.name = trimmed;
      }
      const r = await api.post(`/sessions/${sessionId}/add-guest`, payload);
      if (r.data.mode === 'member_added' || r.data.mode === 'guest_added') {
        toast.success(r.data.message || `${r.data.user?.name} added`);
        onSuccess?.(r.data);
        onClose?.();
      } else if (r.data.mode === 'added_silent') {
        toast.success(r.data.message || 'Guest added silently — no invite sent');
        onSuccess?.(r.data);
        onClose?.();
      } else if (r.data.mode === 'invite_required') {
        // shouldn't reach here since lookup already showed not_registered, but be safe
        setLookupState({
          status: 'not_registered',
          user: null,
          invite: { text: r.data.invite_text, url: r.data.invite_url, phone: r.data.phone },
        });
        toast.warning('User not on SplitEasy — share the invite link');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, phone, silentName, onClose, onSuccess]);

  const doAddWalkIn = useCallback(async () => {
    const cleanName = walkInName.trim();
    if (!cleanName) {
      toast.error('Enter a name for the walk-in');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/add-guest`, { name: cleanName });
      toast.success(r.data.message || `Walk-in '${cleanName}' added`);
      onSuccess?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, walkInName, onClose, onSuccess]);

  const onInviteShare = useCallback(() => {
    const inv = lookupState.invite;
    if (!inv?.url) return;
    window.open(inv.url, '_blank', 'noopener,noreferrer');
  }, [lookupState]);

  const showWalkInPanel = walkInMode;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="add-guest-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" />
            Add to {sessionTitle || 'session'}
          </DialogTitle>
          <DialogDescription>
            Prefer a mobile number — their fees stay tied to that number. Guests never join the group.
          </DialogDescription>
        </DialogHeader>

        {!showWalkInPanel ? (
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="guest-phone" className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Mobile number
              </Label>
              <Input
                id="guest-phone"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="98200 30046"
                autoFocus
                data-testid="add-guest-phone-input"
                className="mt-1 text-base"
              />
            </div>

            {/* Lookup feedback */}
            {lookupState.status === 'loading' && (
              <div className="rounded-xl border-2 border-border bg-muted/30 p-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="add-guest-lookup-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up…
              </div>
            )}
            {lookupState.status === 'self' && (
              <div className="rounded-xl border-2 border-amber-500/40 bg-amber-500/10 p-3 text-xs" data-testid="add-guest-lookup-self">
                That&apos;s your own number.
              </div>
            )}
            {lookupState.status === 'found' && lookupState.user && (
              <button
                type="button"
                onClick={() => doAddByPhone()}
                disabled={submitting}
                data-testid="add-guest-confirm-member"
                className="w-full rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15 p-3 text-left transition-colors flex items-center gap-3 disabled:opacity-50"
              >
                <span className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 inline-flex items-center justify-center text-sm font-bold">
                  {(lookupState.user.name || '?').slice(0, 1).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">
                    Add {lookupState.user.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {lookupState.user.phone} · added to this session
                  </p>
                </div>
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />}
              </button>
            )}
            {lookupState.status === 'not_registered' && lookupState.invite && (
              <div className="rounded-2xl border-2 border-violet-500/40 bg-violet-500/10 p-3 space-y-2" data-testid="add-guest-invite-block">
                <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                  Not on SplitEasy yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Send them a WhatsApp link to sign up in 10 seconds.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full gap-1.5 border-green-500/40 text-green-700 dark:text-green-300 hover:bg-green-500/10"
                  onClick={onInviteShare}
                  data-testid="add-guest-send-invite"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Send WhatsApp invite to {lookupState.invite.phone}
                </Button>
                <div className="pt-1 border-t border-violet-500/20 space-y-1.5">
                  {/* iter207 — optional name input so the chip can read
                      "Rohan" instead of "Guest 9111". */}
                  <Input
                    placeholder="Guest's name (optional)"
                    value={silentName}
                    onChange={(e) => setSilentName(e.target.value)}
                    className="h-8 text-xs"
                    maxLength={40}
                    data-testid="add-guest-silent-name-input"
                  />
                  <button
                    type="button"
                    onClick={() => doAddByPhone({ silent: true })}
                    disabled={submitting}
                    data-testid="add-guest-skip-invite"
                    className="w-full text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-50 py-1"
                  >
                    {submitting ? 'Adding…' : 'Add anyway (skip invite, counts as walk-in)'}
                  </button>
                </div>
              </div>
            )}
            {lookupState.status === 'error' && (
              <div className="rounded-xl border-2 border-red-500/40 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-300">
                Could not look up that number. Check the network and try again.
              </div>
            )}

            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => setWalkInMode(true)}
                data-testid="add-guest-walkin-link"
                className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                No phone? Add as walk-in (cannot be fined) →
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="add-guest-walkin-panel">
            <div className="rounded-xl border-2 border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
              Walk-ins count toward headcount only. They cannot be fined and
              don&apos;t carry a balance.
            </div>
            <div>
              <Label htmlFor="walkin-name" className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
                <UserIcon className="w-3 h-3" /> Walk-in name
              </Label>
              <Input
                id="walkin-name"
                value={walkInName}
                onChange={(e) => setWalkInName(e.target.value)}
                placeholder="e.g. Aman's friend"
                autoFocus
                data-testid="add-guest-walkin-name-input"
                className="mt-1 text-base"
              />
            </div>
            <button
              type="button"
              onClick={() => setWalkInMode(false)}
              data-testid="add-guest-back-to-phone"
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            >
              ← Back to phone
            </button>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="add-guest-cancel">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          {showWalkInPanel && (
            <Button
              onClick={doAddWalkIn}
              disabled={submitting || !walkInName.trim()}
              data-testid="add-guest-walkin-confirm"
              className="gap-1.5"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              {submitting ? 'Adding…' : 'Add walk-in'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
